import { serializeTenantQueryArgs } from '@core/api/tenantContext';
import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQuery } from '@core/api/baseQuery';

/**
 * AI API using RTK Query for conversations
 */
export const aiApi = createApi({
  serializeQueryArgs: serializeTenantQueryArgs,
  reducerPath: 'aiApi',
  baseQuery,
  tagTypes: ['Conversation', 'Message', 'ChatTurn', 'AgentStatus', 'AgentSoul', 'AgentSkills', 'AgentLearnings', 'AgentMemory'],
  endpoints: (builder) => ({
    // Create a new conversation (optionally scoped to a project)
    createConversation: builder.mutation({
      query: (projectUuid) => ({
        url: '/api/ai/conversations/',
        method: 'POST',
        body: projectUuid ? { project_uuid: projectUuid } : {},
      }),
      invalidatesTags: [{ type: 'Conversation', id: 'LIST' }],
    }),

    // Get conversations, optionally filtered by project
    getConversations: builder.query({
      query: (projectUuid) => {
        const params = new URLSearchParams();
        if (projectUuid !== undefined) {
          params.set('project', projectUuid);
        }
        const qs = params.toString();
        return `/api/ai/conversations/${qs ? `?${qs}` : ''}`;
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ uuid }) => ({ type: 'Conversation', id: uuid })),
              { type: 'Conversation', id: 'LIST' },
            ]
          : [{ type: 'Conversation', id: 'LIST' }],
    }),

    // Get messages for a conversation
    getMessages: builder.query({
      query: (conversationUuid) =>
        `/api/ai/conversations/${conversationUuid}/messages/`,
      providesTags: (result, _error, conversationUuid) =>
        result
          ? [
              ...result.map(({ uuid }) => ({ type: 'Message', id: uuid })),
              { type: 'Message', id: conversationUuid },
            ]
          : [{ type: 'Message', id: conversationUuid }],
    }),

    // Start an agent turn on Celery (#256). Returns { message_uuid, task_id } at
    // once; the assistant row is polled with getChatTurn until it settles.
    // Deliberately no Message invalidation: the optimistic echo owns the
    // in-flight render, and a mid-turn refetch would clobber the placeholder.
    startTurn: builder.mutation({
      query: ({ conversationUuid, content, canvasDocumentUuid, canvasPage }) => ({
        url: `/api/ai/conversations/${conversationUuid}/messages/turn/`,
        method: 'POST',
        body: {
          content,
          ...(canvasDocumentUuid && { canvas_document_uuid: canvasDocumentUuid }),
          ...(canvasDocumentUuid && canvasPage && { canvas_page: canvasPage }),
        },
      }),
      invalidatesTags: [{ type: 'Conversation', id: 'LIST' }],
    }),

    // The small payload polled every 1.5 s while a turn runs.
    getChatTurn: builder.query({
      query: ({ conversationUuid, messageUuid }) =>
        `/api/ai/conversations/${conversationUuid}/messages/${messageUuid}/turn/`,
      providesTags: (_result, _error, { messageUuid }) => [{ type: 'ChatTurn', id: messageUuid }],
    }),

    // Stop a running turn. Idempotent server-side; the turn is over afterwards,
    // so the real rows are fetched.
    cancelTurn: builder.mutation({
      query: ({ conversationUuid, messageUuid }) => ({
        url: `/api/ai/conversations/${conversationUuid}/messages/${messageUuid}/cancel/`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { conversationUuid, messageUuid }) => [
        { type: 'Message', id: conversationUuid },
        { type: 'ChatTurn', id: messageUuid },
      ],
    }),

    // Answer AI questions
    answerQuestions: builder.mutation({
      query: ({ conversationUuid, messageUuid, answers }) => ({
        url: `/api/ai/conversations/${conversationUuid}/messages/${messageUuid}/answer/`,
        method: 'POST',
        body: { answers },
      }),
      invalidatesTags: (_result, _error, { conversationUuid }) => [
        { type: 'Message', id: conversationUuid },
      ],
    }),

    // Agent status
    getAgentStatus: builder.query({
      query: () => '/api/ai/agent/status/',
      providesTags: ['AgentStatus'],
    }),

    // Agent onboarding
    agentOnboarding: builder.mutation({
      query: (body) => ({
        url: '/api/ai/agent/onboarding/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['AgentStatus'],
    }),

    // Agent SOUL
    getAgentSoul: builder.query({
      query: () => '/api/ai/agent/soul/',
      providesTags: ['AgentSoul'],
    }),

    updateAgentSoul: builder.mutation({
      query: (body) => ({
        url: '/api/ai/agent/soul/',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['AgentSoul'],
    }),

    // Agent skills
    getAgentSkills: builder.query({
      query: () => '/api/ai/agent/skills/',
      providesTags: ['AgentSkills'],
    }),

    // Agent learnings
    getAgentLearnings: builder.query({
      query: () => '/api/ai/agent/learnings/',
      providesTags: ['AgentLearnings'],
    }),

    // Corporate memory (organization-wide Knowledge Base)
    getAgentMemory: builder.query({
      query: (search = '') => ({
        url: '/api/ai/agent/memory/',
        params: search ? { q: search } : undefined,
      }),
      providesTags: (result) => {
        const pages = Array.isArray(result) ? result : result?.results || result?.pages || [];
        return [
          ...pages.map(({ name }) => ({ type: 'AgentMemory', id: name })),
          { type: 'AgentMemory', id: 'LIST' },
        ];
      },
    }),

    getAgentMemoryPage: builder.query({
      query: (name) => `/api/ai/agent/memory/${encodeURIComponent(name)}/`,
      providesTags: (_result, _error, name) => [{ type: 'AgentMemory', id: name }],
    }),

    updateAgentMemoryPage: builder.mutation({
      query: ({ name, ...body }) => ({
        url: `/api/ai/agent/memory/${encodeURIComponent(name)}/`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, { name }) => [
        { type: 'AgentMemory', id: name },
        { type: 'AgentMemory', id: 'LIST' },
      ],
    }),

    forgetAgentMemoryPage: builder.mutation({
      query: (name) => ({
        url: `/api/ai/agent/memory/${encodeURIComponent(name)}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, name) => [
        { type: 'AgentMemory', id: name },
        { type: 'AgentMemory', id: 'LIST' },
      ],
    }),

    restoreAgentMemoryPage: builder.mutation({
      query: (name) => ({
        url: `/api/ai/agent/memory/${encodeURIComponent(name)}/restore/`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, name) => [
        { type: 'AgentMemory', id: name },
        { type: 'AgentMemory', id: 'LIST' },
      ],
    }),

    // Voice transcription
    transcribeAudio: builder.mutation({
      query: ({ audioBlob, projectUuid, audioSeconds }) => {
        const formData = new FormData();
        formData.append('audio', audioBlob, 'recording.webm');
        if (projectUuid) formData.append('project_uuid', projectUuid);
        if (audioSeconds) formData.append('audio_seconds', String(audioSeconds));
        return {
          url: '/api/ai/transcribe/',
          method: 'POST',
          body: formData,
        };
      },
    }),

    // WOPI token for agent-generated files (Collabora preview)
    getAgentFileWopiToken: builder.query({
      query: ({ orgUuid, filename }) =>
        `/api/ai/agent-files/wopi-token/?org_uuid=${orgUuid}&filename=${encodeURIComponent(filename)}`,
    }),
  }),
});

export const {
  useCreateConversationMutation,
  useGetConversationsQuery,
  useGetMessagesQuery,
  useStartTurnMutation,
  useGetChatTurnQuery,
  useCancelTurnMutation,
  useAnswerQuestionsMutation,
  useGetAgentStatusQuery,
  useAgentOnboardingMutation,
  useGetAgentSoulQuery,
  useUpdateAgentSoulMutation,
  useGetAgentSkillsQuery,
  useGetAgentLearningsQuery,
  useGetAgentMemoryQuery,
  useGetAgentMemoryPageQuery,
  useUpdateAgentMemoryPageMutation,
  useForgetAgentMemoryPageMutation,
  useRestoreAgentMemoryPageMutation,
  useTranscribeAudioMutation,
  useLazyGetAgentFileWopiTokenQuery,
} = aiApi;
